import { Link } from "react-router-dom"
import { LegalList, LegalPage, LegalSection } from "../lib/shared"
import { Seo, breadcrumbSchema } from "../lib/seo"
import { SITE } from "../lib/site"

/** Bump whenever the substance of these terms changes. */
export const TERMS_UPDATED = "2026-09-15"

export default function Terms() {
  return (
    <>
      <Seo
        title="Terms of Service"
        description="The terms that govern use of the Sableboxx website and the basis on which we quote for, contract and deliver design and development work."
        jsonLd={[breadcrumbSchema([{ name: "Terms of Service", path: "/terms" }])]}
      />
      <LegalPage
        title="Terms of Service"
        updated={TERMS_UPDATED}
        intro="These terms cover two things: using this website, and the basis on which Sableboxx quotes for and delivers work. Where a signed proposal says something different, the proposal wins."
      >
        <LegalSection n={1} heading="Agreeing to these terms">
          <p>
            By using sableboxx.com you accept these terms. If you do not accept
            them, please do not use the site. These terms are between you and
            Sableboxx, a software and digital infrastructure company operating from{" "}
            {SITE.address.city}, {SITE.address.countryName}.
          </p>
        </LegalSection>

        <LegalSection n={2} heading="What this site is">
          <p>
            This site describes our services and publishes our work and writing.
            Everything on it is provided for general information. Nothing here
            is an offer capable of acceptance, a quote, or professional advice
            for your particular situation. A quote is a document we send you,
            signed by us.
          </p>
          <p>
            Case studies describe outcomes for specific clients under specific
            conditions. They are honest accounts of what happened, not a promise
            of what will happen for you.
          </p>
        </LegalSection>

        <LegalSection n={3} heading="Using the site">
          <p>You agree not to:</p>
          <LegalList
            items={[
              "Attempt to gain unauthorised access to any part of the site, its infrastructure or the admin dashboard.",
              "Scrape, mirror or republish the site's content at scale, or use it to train a model, without our written permission.",
              "Submit the enquiry form with someone else's details, or use it to send unsolicited commercial messages.",
              "Interfere with the site's operation or use it in any way that breaks applicable law.",
            ]}
          />
          <p>
            We may suspend access from any source that does these things, without
            notice.
          </p>
        </LegalSection>

        <LegalSection n={4} heading="Enquiries and quotes">
          <p>
            Submitting the form at <Link to="/contact">/contact</Link> starts a
            conversation; it does not create a contract or reserve capacity. We
            aim to reply within two working days. We may decline any enquiry,
            and we do not have to give a reason.
          </p>
          <p>
            Any figure we discuss before a written proposal is an indication
            only. A binding price exists once we issue a proposal and you accept
            it in writing.
          </p>
        </LegalSection>

        <LegalSection n={5} heading="Engagements">
          <p>
            Project work is governed by the proposal or statement of work we
            both sign. That document sets the scope, the fee, the schedule, the
            payment terms and the number of revision rounds included. Where it
            conflicts with this page, it prevails.
          </p>
          <LegalList
            items={[
              <>
                <strong>Deposits.</strong> Unless the proposal says otherwise,
                work begins on receipt of the deposit, and the deposit is
                non-refundable once work has begun.
              </>,
              <>
                <strong>Your input.</strong> Delivery dates assume you supply
                content, feedback and approvals when the schedule asks for them.
                Delay on your side moves our dates by at least the same amount.
              </>,
              <>
                <strong>Changes.</strong> Work outside the agreed scope is
                quoted separately and started only once you approve it in
                writing.
              </>,
              <>
                <strong>Ending an engagement.</strong> Either of us may end an
                engagement with 14 days&rsquo; written notice. You pay for work
                completed and costs committed up to that point; we hand over
                what has been paid for.
              </>,
            ]}
          />
        </LegalSection>

        <LegalSection n={6} heading="Intellectual property">
          <p>
            <strong>The site.</strong> The Sableboxx name, logo, written content,
            design and code on this site belong to us and are protected by
            copyright and trade mark law. Reading it grants you no licence to
            reuse it.
          </p>
          <p>
            <strong>Project deliverables.</strong> Ownership of final approved
            deliverables transfers to you when you have paid in full. Until then
            we retain ownership, and anything unpaid may not be used.
          </p>
          <p>
            <strong>What stays ours.</strong> Concepts not selected, working
            files, and the tools, frameworks and internal components we reuse
            across clients remain ours. Where a deliverable depends on one of
            those, you get a perpetual licence to use it as part of the
            deliverable.
          </p>
          <p>
            <strong>Third-party assets.</strong> Fonts, stock imagery and
            plugins are licensed, not owned. We tell you which ones a project
            depends on and what the licence costs; those licences are yours to
            hold and renew.
          </p>
          <p>
            <strong>Showing the work.</strong> We may display completed work in
            our portfolio and marketing unless the proposal says we may not, and
            we will always honour an agreed confidentiality period first.
          </p>
        </LegalSection>

        <LegalSection n={7} heading="Payment">
          <p>
            Invoices are due within the period stated on the invoice, 14 days if
            it states none. Fees are exclusive of VAT and of bank or
            currency-conversion charges, which are yours. We may suspend work on
            overdue accounts and charge interest at 2% per month on amounts more
            than 30 days late.
          </p>
        </LegalSection>

        <LegalSection n={8} heading="Warranties and liability">
          <p>
            We warrant that we will perform our work with reasonable skill and
            care, to a professional standard. Beyond that the site itself is
            provided &ldquo;as is&rdquo;: we do not warrant that it will be
            uninterrupted, error-free, or that it is free of anything harmful.
          </p>
          <p>
            We are not liable for indirect or consequential loss, lost profits,
            lost revenue, lost data or loss of goodwill. Our total liability
            arising from an engagement is limited to the fees you have paid us
            under that engagement. Nothing here limits liability for death or
            personal injury caused by negligence, for fraud, or for anything
            else that cannot lawfully be limited.
          </p>
          <p>
            After launch, a site depends on hosting, domains, third-party
            services and updates. Unless you have a support agreement with us,
            keeping those current is yours to manage.
          </p>
        </LegalSection>

        <LegalSection n={9} heading="Confidentiality">
          <p>
            Each of us will keep the other&rsquo;s non-public business
            information confidential and use it only for the engagement. This
            survives the end of the engagement by three years.
          </p>
        </LegalSection>

        <LegalSection n={10} heading="Privacy">
          <p>
            How we handle personal data is set out in our{" "}
            <Link to="/privacy">Privacy Policy</Link>, which forms part of these
            terms.
          </p>
        </LegalSection>

        <LegalSection n={11} heading="Governing law">
          <p>
            These terms are governed by the laws of the Federal Republic of
            Nigeria, and the courts of Nigeria have exclusive jurisdiction. If a
            dispute arises we both agree to attempt to resolve it in good faith,
            in writing, before starting proceedings.
          </p>
        </LegalSection>

        <LegalSection n={12} heading="Changes and contact">
          <p>
            We may update these terms; the date at the top of this page shows
            when they last changed, and continued use of the site after that
            means you accept the change. Changes do not alter the terms of an
            engagement already signed.
          </p>
          <p>
            Questions go to <a href={`mailto:${SITE.email}`}>{SITE.email}</a> or{" "}
            <a href={`tel:${SITE.phoneHref}`}>{SITE.phone}</a>.
          </p>
        </LegalSection>
      </LegalPage>
    </>
  )
}
